// =============================================================================
// Absences Tab - Gestione rapida e centralizzata di assenze / presenze aggiuntive
// =============================================================================
// Permette di inserire e modificare le eccezioni alla disponibilità di TUTTE le
// risorse da un unico posto, senza dover entrare nell'editor di ogni risorsa.

import { useMemo, useState } from 'react';
import { Plus, Loader2, CalendarOff, Tags, ChevronDown } from 'lucide-react';
import { EmptyState } from '../common';
import { useTranslation } from 'react-i18next';
import {
  useAllExceptions,
  useResources,
  useDeleteResourceException,
  ResourceException,
} from '../../api/hooks';
import { ExceptionForm } from './ExceptionForm';
import { ExceptionRow } from './ExceptionRow';
import { AbsenceReasonsManager } from './AbsenceReasonsManager';
import { Toggle } from '../ui/Toggle';

export function AbsencesTab() {
  const { t } = useTranslation('settings');
  const [showPast, setShowPast] = useState(false);

  const { data: exceptions, isLoading } = useAllExceptions(!showPast);
  const { data: resources } = useResources();
  const deleteException = useDeleteResourceException();

  const [editing, setEditing] = useState<ResourceException | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showReasons, setShowReasons] = useState(false);

  const resourceMap = useMemo(() => {
    const map = new Map<string, { name: string; color: string | null }>();
    for (const r of resources ?? []) {
      map.set(r.id, { name: r.name, color: r.color ?? null });
    }
    return map;
  }, [resources]);

  const handleDelete = async (exc: ResourceException) => {
    const label = exc.title || exc.reason?.name || t('exceptions.absence');
    if (!window.confirm(t('exceptions.deleteConfirm', { title: label }))) return;
    try {
      await deleteException.mutateAsync({ resourceId: exc.resourceId, exceptionId: exc.id });
    } catch (error) {
      console.error('Error deleting exception:', error);
    }
  };

  const closeForm = () => {
    setEditing(null);
    setIsCreating(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t('absences.title')}</h2>
          <p className="text-sm text-gray-500 mt-0.5">{t('absences.subtitle')}</p>
        </div>
        {!isCreating && !editing && (
          <button
            onClick={() => setIsCreating(true)}
            className="btn-primary flex items-center gap-2 px-4 py-2 text-sm flex-shrink-0"
          >
            <Plus className="w-4 h-4" />
            {t('absences.add')}
          </button>
        )}
      </div>

      {/* Gestione causali (collassabile) */}
      {!isCreating && !editing && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <button
            onClick={() => setShowReasons((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 text-left"
          >
            <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Tags className="w-4 h-4 text-gray-400" />
              {t('absenceReasons.title')}
            </span>
            <ChevronDown
              className={`w-4 h-4 text-gray-400 transition-transform ${showReasons ? 'rotate-180' : ''}`}
            />
          </button>
          {showReasons && (
            <div className="px-4 pb-4">
              <p className="text-sm text-gray-500 mb-3">{t('absenceReasons.subtitle')}</p>
              <AbsenceReasonsManager />
            </div>
          )}
        </div>
      )}

      {/* Form inline (creazione/modifica) */}
      {(isCreating || editing) && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <ExceptionForm
            resources={resources ?? []}
            exception={editing}
            onClose={closeForm}
          />
        </div>
      )}

      {/* Lista */}
      {!isCreating && !editing && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-medium text-gray-700">
              {exceptions?.length ?? 0} {t('absences.count')}
            </span>
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <Toggle checked={showPast} onChange={() => setShowPast((v) => !v)} />
              {t('absences.showPast')}
            </label>
          </div>

          {isLoading ? (
            <div className="py-12 text-center">
              <Loader2 className="w-6 h-6 text-emerald-600 animate-spin mx-auto" />
            </div>
          ) : !exceptions || exceptions.length === 0 ? (
            <EmptyState icon={CalendarOff} title={t('absences.empty')} size="sm" className="py-12" />
          ) : (
            <ul className="space-y-2 p-4">
              {exceptions.map((exc) => {
                const res = resourceMap.get(exc.resourceId);
                return (
                  <ExceptionRow
                    key={exc.id}
                    exception={exc}
                    resourceLabel={res?.name ?? t('absences.unknownResource')}
                    resourceColor={res?.color}
                    onEdit={() => setEditing(exc)}
                    onDelete={() => handleDelete(exc)}
                    deleting={deleteException.isPending}
                  />
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
